import { useState } from "react";
import { StyleSheet, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import { CoachGrant } from "@heraion/shared";

import type { UiTheme } from "../theme";
import { ToggleRow } from "../primitives/ToggleRow";

// #27: what one coach may do with the athlete's data, a row per
// permission, each the athlete's to give or take back. A list, not one
// button: health permissions join it after the first release (#155).
// The host injects the write; this component knows no API.

// Typed by the enum, so a new grant does not compile without its words.
const GRANT_COPY: Record<
  CoachGrant,
  { key: string; label: string; subKey: string; sub: string }
> = {
  "write:targets": {
    key: "coach.grants.writeTargets",
    label: "Set your weekly targets",
    subKey: "coach.grants.writeTargetsSub",
    sub: "Each change stays in your targets history.",
  },
};

export type CoachGrantsResult = { ok: boolean; code?: string };

export type CoachGrantsListProps = {
  theme: UiTheme;
  /** What this coach may do now (edge.grants). */
  grants: ReadonlyArray<CoachGrant>;
  /** Save the whole list. Resolves once the host holds the new edge. */
  onChange: (next: CoachGrant[]) => Promise<CoachGrantsResult>;
};

export function CoachGrantsList({ theme, grants, onChange }: CoachGrantsListProps) {
  const { t } = useTranslation();
  const styles = makeStyles(theme);
  const [pending, setPending] = useState<CoachGrant[] | null>(null);
  // The error belongs to the list it failed on: a new list from the host
  // (a refresh, another tab) hides it.
  const grantsKey = grants.join(",");
  const [err, setErr] = useState<{ code: string; on: string } | null>(null);
  const errCode = err?.on === grantsKey ? err.code : null;
  const shown = pending ?? grants;

  const toggle = async (grant: CoachGrant, on: boolean) => {
    if (pending) return;
    const next = CoachGrant.options.filter((g) =>
      g === grant ? on : grants.includes(g),
    );
    setPending(next);
    setErr(null);
    const r = await onChange(next);
    setPending(null);
    if (!r.ok) setErr({ code: r.code ?? "io_error", on: grantsKey });
  };

  return (
    <View style={styles.wrap}>
      <Text style={styles.heading}>
        {t("coach.grants.heading", { defaultValue: "What this coach can do" })}
      </Text>
      <Text style={styles.intro}>
        {t("coach.grants.readOnly", {
          defaultValue:
            "They can read your training. They change nothing unless you allow it here.",
        })}
      </Text>
      {CoachGrant.options.map((grant) => {
        const copy = GRANT_COPY[grant];
        return (
          <ToggleRow
            key={grant}
            theme={theme}
            label={t(copy.key, { defaultValue: copy.label })}
            sublabel={t(copy.subKey, { defaultValue: copy.sub })}
            value={shown.includes(grant)}
            onValueChange={(on) => toggle(grant, on)}
            disabled={pending !== null}
            busy={pending !== null}
          />
        );
      })}
      {errCode ? (
        <Text
          accessibilityRole="alert"
          accessibilityLiveRegion="polite"
          style={styles.error}
        >
          {t(`coach.errCode.${errCode}`, {
            defaultValue: t("coach.errCode.io_error"),
          })}
        </Text>
      ) : null}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s1, paddingTop: s.s2 },
    heading: { color: p.ink2, fontSize: ty.sizes.sm, fontWeight: "700" },
    intro: { color: p.ink3, fontSize: ty.sizes.sm },
    error: { color: p.danger, fontSize: ty.sizes.sm },
  });
}
